import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getUrgentAppeals,
  getUrgentAppeal,
  createUrgentAppeal,
  updateUrgentAppeal,
  deleteUrgentAppeal,
  updateUrgentAppealOrder
} from 'lib/supabaseQueries';
import { useAuthContext } from 'context/authContext';
import useBranchPermission from './useBranchPermission';
import { toast } from 'react-toastify';

// Hook to fetch all urgent appeals
export function useUrgentAppeals() {
  const { organization, isInitialized } = useAuthContext();
  const { branchId } = useBranchPermission();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['urgentAppeals', organization?.id, branchId],
    queryFn: () => getUrgentAppeals(organization.id, branchId),
    enabled: isInitialized && !!organization?.id
  });

  return {
    urgentAppeals: data?.data || [],
    isLoading,
    error: error || data?.error,
    refetch
  };
}

// Hook to fetch single urgent appeal
export function useUrgentAppeal(id) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['urgentAppeal', id],
    queryFn: () => getUrgentAppeal(id),
    enabled: !!id
  });

  return {
    urgentAppeal: data?.data || null,
    isLoading,
    error: error || data?.error,
    refetch
  };
}

// Hook to create urgent appeal
export function useCreateUrgentAppeal() {
  const queryClient = useQueryClient();
  const { branchId } = useBranchPermission();

  const mutation = useMutation({
    mutationFn: async appealData => {
      const result = await createUrgentAppeal({
        ...appealData,
        branch_id: branchId
      });
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['urgentAppeals'] });
      toast.success('Urgent appeal created successfully');
    },
    onError: error => {
      console.error('Create urgent appeal error:', error);
      toast.error('Failed to create urgent appeal');
    }
  });

  return {
    createUrgentAppeal: mutation.mutate,
    createUrgentAppealAsync: mutation.mutateAsync,
    isCreating: mutation.isPending,
    error: mutation.error
  };
}

// Hook to update urgent appeal
export function useUpdateUrgentAppeal() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const result = await updateUrgentAppeal(id, data);
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
    onSuccess: async (data, variables) => {
      await queryClient.invalidateQueries({ queryKey: ['urgentAppeals'] });
      await queryClient.invalidateQueries({
        queryKey: ['urgentAppeal', variables.id]
      });
      toast.success('Urgent appeal updated successfully');
    },
    onError: error => {
      console.error('Update urgent appeal error:', error);
      toast.error('Failed to update urgent appeal');
    }
  });

  return {
    updateUrgentAppeal: mutation.mutate,
    updateUrgentAppealAsync: mutation.mutateAsync,
    isUpdating: mutation.isPending,
    error: mutation.error
  };
}

// Hook to update urgent appeal display order
export function useUpdateUrgentAppealOrder() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async updates => {
      const result = await updateUrgentAppealOrder(updates);
      if (result.error) throw new Error(result.error);
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['urgentAppeals'] });
    },
    onError: error => {
      toast.error(error.message || 'Failed to update appeal order');
    }
  });
  return {
    updateAppealOrder: mutation.mutateAsync,
    isUpdating: mutation.isPending
  };
}

// Hook to delete urgent appeal
export function useDeleteUrgentAppeal() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async id => {
      const result = await deleteUrgentAppeal(id);
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['urgentAppeals'] });
      toast.success('Urgent appeal deleted successfully');
    },
    onError: error => {
      console.error('Delete urgent appeal error:', error);
      toast.error('Failed to delete urgent appeal');
    }
  });

  return {
    deleteUrgentAppeal: mutation.mutate,
    deleteUrgentAppealAsync: mutation.mutateAsync,
    isDeleting: mutation.isPending,
    error: mutation.error
  };
}
